import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Robot, PlugsConnected, Plugs, ShieldCheck, Warning, CheckCircle } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { mt5Brokers, mt5Status, mt5Connect, mt5Settings, mt5Disconnect, fmtApiError } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const STATUS_STYLES = {
  pending:      { label: "Pending approval", cls: "text-amber-300 border-amber-400/40 bg-amber-400/10", Icon: Warning },
  approved:     { label: "Approved · Live",  cls: "text-bull border-bull/40 bg-bull/10",                 Icon: ShieldCheck },
  paused:       { label: "Paused by admin",  cls: "text-amber-300 border-amber-400/40 bg-amber-400/10", Icon: Warning },
  rejected:     { label: "Rejected",         cls: "text-bear border-bear/40 bg-bear/10",                 Icon: Warning },
  disconnected: { label: "Disconnected",     cls: "text-zinc-400 border-white/15 bg-white/5",            Icon: Plugs },
  disabled:     { label: "Disabled by admin",cls: "text-bear border-bear/40 bg-bear/10",                 Icon: Warning },
};

function Section({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <div className="surface p-6 mb-5" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <Icon size={20} className="text-gold mt-0.5" />
        <div>
          <div className="heading text-lg font-semibold tracking-tight">{title}</div>
          {subtitle && <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

const inputCls = "mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none rounded";

export default function AutoTradingPage() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: brokersData } = useQuery({ queryKey: ["mt5-brokers"], queryFn: mt5Brokers });
  const { data: status, isLoading } = useQuery({ queryKey: ["mt5-status"], queryFn: mt5Status, refetchInterval: 8000 });

  const brokers = brokersData?.brokers || [];
  const account = status?.account;
  const settings = status?.settings;
  const ccy = status?.currency || "USD";
  const hasBalance = status?.balance != null || status?.equity != null;
  const fmtMoney = (v) => v == null ? "—" : `${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

  // ── connect form ──
  const [form, setForm] = useState({
    broker_id: "", login: "", password: "", server: "",
    lot: 0.05, tp_mode: "partial", tp1_percent: 40, break_even: true, max_trades: 3,
  });
  const selectedBroker = brokers.find((b) => b.id === form.broker_id);

  useEffect(() => {
    if (brokers.length && !form.broker_id) {
      setForm((f) => ({ ...f, broker_id: brokers[0].id, server: brokers[0].servers?.[0] || "" }));
    }
  }, [brokers, form.broker_id]);

  const connectMut = useMutation({
    mutationFn: mt5Connect,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mt5-status"] });
      toast({ title: "Submitted", description: "Your MT5 account is pending admin approval." });
      setForm((f) => ({ ...f, password: "" }));
    },
    onError: (e) => toast({ title: "Connection failed", description: fmtApiError(e?.response?.data?.detail), variant: "destructive" }),
  });

  const settingsMut = useMutation({
    mutationFn: mt5Settings,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mt5-status"] }); toast({ title: "Settings saved" }); },
  });

  const disconnectMut = useMutation({
    mutationFn: mt5Disconnect,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mt5-status"] }); toast({ title: "Disconnected" }); },
  });

  const submitConnect = (e) => {
    e.preventDefault();
    connectMut.mutate({ ...form, lot: Number(form.lot), tp1_percent: Number(form.tp1_percent), max_trades: Number(form.max_trades) });
  };

  const st = account ? (STATUS_STYLES[account.status] || STATUS_STYLES.disconnected) : null;

  return (
    <AppShell>
      <div className="p-6 md:p-10 max-w-3xl" data-testid="auto-trading-page">
        <div className="overline mb-3">Automation</div>
        <h1 className="heading text-3xl md:text-5xl font-bold tracking-tighter mb-2">
          Auto <span className="text-gold-grad">Trading.</span>
        </h1>
        <p className="text-sm text-zinc-500 mb-8 max-w-xl">
          Mirror Daddy Of AI signals straight to your MT5 account. Entry, SL & TP come from the AI engine —
          you only control lot size, TP behaviour and risk limits.
        </p>

        {isLoading ? (
          <Loader2 className="size-5 animate-spin text-white/50" />
        ) : (
          <>
            {/* Live account panel */}
            {account && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="mt5-account-panel">
                <StatCard label="Balance" value={hasBalance ? fmtMoney(status.balance) : "—"} testId="mt5-balance" />
                <StatCard label="Equity"  value={hasBalance ? fmtMoney(status.equity)  : "—"} testId="mt5-equity" />
                <StatCard label="Open P/L"
                  value={fmtMoney(status?.open_pnl ?? 0)}
                  accent={Number(status?.open_pnl) >= 0 ? "text-bull" : "text-bear"}
                  testId="mt5-open-pnl" />
                <StatCard label="Open trades" value={String(status?.open_count ?? 0)} testId="mt5-open-count" />
              </div>
            )}
            {account && !hasBalance && (
              <div className="text-[11px] text-zinc-500 -mt-3 mb-5">
                Balance & equity populate once your MT5 worker connects and reports a snapshot.
              </div>
            )}

            {/* Current status */}
            {account && st && (
              <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border mb-5 ${st.cls}`} data-testid="mt5-status-banner">
                <st.Icon size={20} />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{st.label}</div>
                  <div className="text-xs opacity-80">{account.broker_name} · login {account.login} · {account.server}</div>
                </div>
                {(account.status !== "disconnected") && (
                  <button
                    data-testid="mt5-disconnect-btn"
                    onClick={() => disconnectMut.mutate()}
                    className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-white/15 hover:bg-white/10 rounded"
                  >
                    Disconnect
                  </button>
                )}
              </div>
            )}

            {/* Connection form */}
            <Section icon={PlugsConnected} title={account ? "Update connection" : "Connect MT5 account"}
              subtitle="Credentials are AES-256 encrypted at rest." testId="mt5-connect-section">
              <form onSubmit={submitConnect} className="grid sm:grid-cols-2 gap-4">
                <label className="block text-xs text-zinc-400 sm:col-span-2">
                  Broker
                  <select
                    data-testid="mt5-broker-select"
                    className={inputCls}
                    value={form.broker_id}
                    onChange={(e) => {
                      const b = brokers.find((x) => x.id === e.target.value);
                      setForm((f) => ({ ...f, broker_id: e.target.value, server: b?.servers?.[0] || "" }));
                    }}
                  >
                    {brokers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </label>
                <label className="block text-xs text-zinc-400">
                  Login
                  <input data-testid="mt5-login-input" className={inputCls} value={form.login}
                    onChange={(e) => setForm((f) => ({ ...f, login: e.target.value }))} placeholder="12345678" />
                </label>
                <label className="block text-xs text-zinc-400">
                  Password
                  <input data-testid="mt5-password-input" type="password" className={inputCls} value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
                </label>
                <label className="block text-xs text-zinc-400 sm:col-span-2">
                  Server
                  {selectedBroker?.servers?.length ? (
                    <select data-testid="mt5-server-select" className={inputCls} value={form.server}
                      onChange={(e) => setForm((f) => ({ ...f, server: e.target.value }))}>
                      {selectedBroker.servers.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input data-testid="mt5-server-input" className={inputCls} value={form.server}
                      onChange={(e) => setForm((f) => ({ ...f, server: e.target.value }))} placeholder="Broker-Server" />
                  )}
                </label>

                <label className="block text-xs text-zinc-400">
                  Lot size
                  <input data-testid="mt5-lot-input" type="number" step="0.01" className={inputCls} value={form.lot}
                    onChange={(e) => setForm((f) => ({ ...f, lot: e.target.value }))} />
                </label>
                <label className="block text-xs text-zinc-400">
                  TP mode
                  <select data-testid="mt5-tpmode-select" className={inputCls} value={form.tp_mode}
                    onChange={(e) => setForm((f) => ({ ...f, tp_mode: e.target.value }))}>
                    <option value="tp1">TP1 only</option>
                    <option value="tp2">TP2 only</option>
                    <option value="partial">Partial (TP1 + runner)</option>
                  </select>
                </label>
                <label className="block text-xs text-zinc-400">
                  TP1 close %
                  <input data-testid="mt5-tp1pct-input" type="number" className={inputCls} value={form.tp1_percent}
                    onChange={(e) => setForm((f) => ({ ...f, tp1_percent: e.target.value }))} />
                </label>
                <label className="block text-xs text-zinc-400">
                  Max trades
                  <input data-testid="mt5-maxtrades-input" type="number" className={inputCls} value={form.max_trades}
                    onChange={(e) => setForm((f) => ({ ...f, max_trades: e.target.value }))} />
                </label>
                <label className="flex items-center gap-2 text-xs text-zinc-400 sm:col-span-2">
                  <input data-testid="mt5-breakeven-check" type="checkbox" checked={form.break_even}
                    onChange={(e) => setForm((f) => ({ ...f, break_even: e.target.checked }))} />
                  Move SL to break-even after TP1
                </label>

                <button type="submit" disabled={connectMut.isPending}
                  data-testid="mt5-connect-submit"
                  className="sm:col-span-2 px-4 py-2.5 mono text-[11px] uppercase tracking-widest bg-gold text-black hover:bg-yellow-400 disabled:opacity-50 rounded inline-flex items-center justify-center gap-2">
                  {connectMut.isPending && <Loader2 className="size-4 animate-spin" />}
                  {account ? "Update & resubmit" : "Connect"}
                </button>
              </form>
            </Section>

            {/* Live settings (only when an account exists) */}
            {account && settings && (
              <Section icon={Robot} title="Trading controls" subtitle="Changes apply to the next signal." testId="mt5-settings-section">
                <label className="flex items-start gap-4 py-3 border-b border-white/5">
                  <button type="button" role="switch" aria-checked={!!settings.autotrading}
                    data-testid="mt5-autotrading-toggle"
                    onClick={() => settingsMut.mutate({ autotrading: !settings.autotrading })}
                    className={`mt-1 w-10 h-5 flex items-center px-0.5 rounded-full transition shrink-0 ${settings.autotrading ? "bg-gold" : "bg-zinc-700"}`}>
                    <span className={`w-4 h-4 rounded-full bg-white transition-transform ${settings.autotrading ? "translate-x-5" : ""}`} />
                  </button>
                  <div>
                    <div className="text-sm font-medium">Auto-trading</div>
                    <div className="text-xs text-zinc-500 mt-0.5">When on, every new AI signal is sent to your MT5 worker.</div>
                  </div>
                </label>

                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <SettingField label="Lot size" value={settings.lot} step="0.01" testId="mt5-set-lot"
                    onSave={(v) => settingsMut.mutate({ lot: Number(v) })} />
                  <SettingField label="TP1 close %" value={settings.tp1_percent} testId="mt5-set-tp1"
                    onSave={(v) => settingsMut.mutate({ tp1_percent: Number(v) })} />
                  <SettingField label="Max trades" value={settings.max_trades} testId="mt5-set-max"
                    onSave={(v) => settingsMut.mutate({ max_trades: Number(v) })} />
                  <label className="block text-xs text-zinc-400">
                    TP mode
                    <select data-testid="mt5-set-tpmode" className={inputCls} value={settings.tp_mode}
                      onChange={(e) => settingsMut.mutate({ tp_mode: e.target.value })}>
                      <option value="tp1">TP1 only</option>
                      <option value="tp2">TP2 only</option>
                      <option value="partial">Partial</option>
                    </select>
                  </label>
                </div>
              </Section>
            )}

            {/* Trades */}
            {account && (
              <Section icon={CheckCircle} title="Trade history" subtitle={`${status?.open_count || 0} open`} testId="mt5-trades-section">
                {(status?.trades || []).length === 0 ? (
                  <div className="text-sm text-zinc-500 py-4">No trades yet. Approved signals will appear here once your MT5 worker executes them.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="mt5-trades-table">
                      <thead className="text-zinc-500 text-left">
                        <tr><th className="py-2">Ticket</th><th>Side</th><th>Lot</th><th>Entry</th><th>P/L</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {status.trades.map((t) => (
                          <tr key={t.id} className="border-t border-white/5">
                            <td className="py-2 mono">{t.ticket}</td>
                            <td className={t.side === "BUY" ? "text-bull" : "text-bear"}>{t.side}</td>
                            <td>{t.lot}</td>
                            <td className="mono">{t.entry}</td>
                            <td className={Number(t.profit) >= 0 ? "text-bull" : "text-bear"}>{t.profit}</td>
                            <td>{t.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, accent, testId }) {
  return (
    <div className="surface px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

function SettingField({ label, value, step, onSave, testId }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <label className="block text-xs text-zinc-400">
      {label}
      <div className="flex gap-2">
        <input data-testid={testId} type="number" step={step} className={inputCls} value={v}
          onChange={(e) => setV(e.target.value)} />
        <button type="button" onClick={() => onSave(v)}
          className="mt-1 px-3 mono text-[10px] uppercase tracking-widest border border-white/10 hover:bg-white/5 rounded">Save</button>
      </div>
    </label>
  );
}
